import React, { PureComponent } from 'react';
import { Container, Row } from 'reactstrap';
import If from 'shared/components/SimpleIf';
import DownloadAsPngLink from 'shared/components/DownloadAsPngLink';
import { random5DigitNumber } from 'shared/utils/mathUtil';
import CustomTooltip from 'shared/components/CustomTooltip';
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { StringToColor } from 'shared/utils/graphicUtil';
import { CostTrackingConstants, DisplayMetricTypes } from 'usage/constants/costAndUsageConstants';
import { withUserSettingsConsumer } from 'users/utils/contexts/UserSettingsContext';

class CostChart extends PureComponent {

  constructor(props) {
    super(props);
    this.state = {
      currKey: '',
    };
    this.chartRef = React.createRef();
  }

  localCostStrFromString = (strValue) => {
    const { getCurrencyNumber, displayedMetric } = this.props;
    if (displayedMetric === DisplayMetricTypes.COST) {
      return `${getCurrencyNumber(strValue)}`;
    }
    return strValue;
  };

  handleBarClick = (data, index, dataKey) => {
    if (this.props.barClickable && dataKey !== CostTrackingConstants.OTHERS) {
      const { usageDate } = data;
      this.props.barClickHandler(usageDate, dataKey);
    }
  };

  getStackId = (dataKeyName) => (dataKeyName === this.props.separateStackKeyName ? 'b' : 'a');

  renderArea(dataKeys) {
    const { currKey } = this.state;
    const { barClickable } = this.props;
    return dataKeys.map((dataKey) => {
      //eslint-disable-next-line no-unused-vars
      const customMouseOver = (e) => {
        if (dataKey.name !== currKey) {
          this.setState({ currKey: dataKey.name });
        }
      };
      return (
        <Area
          cursor={barClickable ? 'pointer' : 'default'}
          dataKey={dataKey.name}
          key={dataKey.name}
          stackId={this.getStackId(dataKey.name)}
          stroke={
            dataKey.name === CostTrackingConstants.OTHERS
              ? CostTrackingConstants.OTHERS_NAME_COLOR
              : StringToColor.next(dataKey.name)
          }
          fill={
            dataKey.name === CostTrackingConstants.OTHERS
              ? CostTrackingConstants.OTHERS_NAME_COLOR
              : StringToColor.next(dataKey.name)
          }
          onClick={(data, idx) => this.handleBarClick(data, idx, dataKey.name)}
          onMouseOver={customMouseOver}
          style={barClickable ? { cursor: 'pointer' } : null}
          type="monotone"
        />
      );
    });
  }

  renderBar(dataKeys) {
    const { currKey } = this.state;
    const { barClickable } = this.props;
    return dataKeys.map((dataKey) => {
      //eslint-disable-next-line no-unused-vars
      const customMouseOver = (e) => {
        if (dataKey.name !== currKey) {
          this.setState({ currKey: dataKey.name });
        }
      };
      return (
        <Bar
          cursor={barClickable ? 'pointer' : 'default'}
          barSize={28}
          dataKey={dataKey.name}
          key={dataKey.name}
          stackId={this.getStackId(dataKey.name)}
          fill={
            dataKey.name === CostTrackingConstants.OTHERS
              ? CostTrackingConstants.OTHERS_NAME_COLOR
              : StringToColor.next(dataKey.name)
          }
          onClick={dataKey.name === 'others' ? null : (data, idx) => this.handleBarClick(data, idx, dataKey.name)}
          onMouseOver={customMouseOver}
          style={barClickable ? { cursor: 'pointer' } : null}
        />
      );
    });
  }

  renderLine(dataKeys) {
    return dataKeys.map((dataKey) => {
      return <Line type="monotone" key={dataKey.name} dataKey={dataKey.name} stroke="#ff7300" />;
    });
  }

  render() {
    const {
      data,
      displayedMetric,
      isRenderDownloadAsPng,
      chartId,
      yAxisLabelTitle,
      dataKeys,
      xAxisLabelTitle,
      isTrendLine,
      showAllItems,
      numStrAbriviaionByDisplayMetric,
    } = this.props;
    const { currKey } = this.state;
    const finalChartId = chartId || `cost-chart-${random5DigitNumber()}`;

    return (
      <Container>
        <If cond={isRenderDownloadAsPng}>
          <DownloadAsPngLink chartRef={this.chartRef} />
        </If>
        <Row id={finalChartId} className="cost-chart-container">
          <ResponsiveContainer height={400} ref={this.chartRef}>
            <ComposedChart
              stackOffset="sign"
              width={1000}
              height={400}
              data={data}
              margin={{
                top: 20,
                right: 30,
                left: 20,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis label={{ value: xAxisLabelTitle || '' }} dataKey="usageDate" />
              <YAxis
                tickLine
                label={{
                  value: yAxisLabelTitle || `${displayedMetric}`,
                  offset: 0,
                  angle: -90,
                  position: 'left',
                }}
                tickFormatter={(value) =>
                  numStrAbriviaionByDisplayMetric(value, value, displayedMetric, false, { decimal: 2 })
                }
              />
              <Tooltip
                cursor={false}
                content={
                  <CustomTooltip
                    showAllItems={showAllItems}
                    data={data}
                    currKey={currKey}
                    displayedMetric={displayedMetric}
                  />
                }
              />
              <Legend />
              <ReferenceLine y={0} stroke="#000" />
              {showAllItems ? this.renderArea(dataKeys) : this.renderBar(dataKeys)}
              {isTrendLine ? <Line type="linear" dataKey="totalSum" stroke="#ff7300" /> : null}
            </ComposedChart>
          </ResponsiveContainer>
        </Row>
      </Container>
    );
  }
}

const ObserverCostChart = withUserSettingsConsumer(CostChart);
export default ObserverCostChart;
